/**
 * Screen 36 — the ONE duel creation page.
 *
 * As of the Phase 8.12 rollout, all duels are asynchronous — the
 * classic live-sync path was retired. This page is now mounted at
 * BOTH `/duels/new` (from the browser's + Create chip) and
 * `/play/duel` (from Home's game-mode row), so there's a single
 * creation experience regardless of entry point.
 *
 * Flow (per wireframe):
 *   1. Your-rank card at top (identity + rank + tier).
 *   2. "Publish to" — Everyone (public) vs A friend.
 *   3. Topic pill row.
 *   4. Stake pill row: Free / ₦200 / ₦500 / ₦1,000 / Other. Tapping
 *      "Other" reveals a numeric input for a custom amount (bounded
 *      by CUSTOM_STAKE_MIN_KOBO / CUSTOM_STAKE_MAX_KOBO).
 *   5. "Pay stake with" — Bonus / Main wallet (both show live balance).
 *   6. "How it works" info card.
 *   7. CTA: "Publish & play my round now" → creates + navigates to run.
 *
 * On confirm the backend escrows + starts the creator's run in one
 * atomic step; we navigate straight to `/duel/{id}/play` on success.
 */

import { api, ApiError } from '../api.js';
import { attachCategoryPicker, renderCategoryTrigger } from '../components/category_picker.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';
import { rankLabel } from './duels_browse.js';

const STAKES = [
  { kobo: 0, label: 'Free' },
  { kobo: 20_000, label: '₦200' },
  { kobo: 50_000, label: '₦500' },
  { kobo: 100_000, label: '₦1,000' },
];

// Bounds for the "Other" custom-amount input (kobo). Matches the
// preset floor (₦200) at the low end but relaxes it to ₦100 so a
// player who just wants a token stake isn't forced to a preset.
// The ceiling stops fat-fingered ₦100,000+ stakes from clearing the
// UI before the balance check catches them.
const CUSTOM_STAKE_MIN_KOBO = 10_000; // ₦100
const CUSTOM_STAKE_MAX_KOBO = 1_000_000; // ₦10,000

export async function renderDuelCreate(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/duels" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Create a duel</div>
    </div>
    <div class="pad" id="create-page"><div class="skel"></div><div class="skel"></div></div>
  `;

  const page = document.getElementById('create-page');
  let categories = [];
  let me = null;
  let wallet = null;
  let friends = [];

  try {
    [categories, me, wallet, friends] = await Promise.all([
      api('/game/categories'),
      api('/me'),
      api('/wallet'),
      api('/friends').catch(() => []),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    page.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  // Pull rank from /leaderboard/me — a light call that already exists.
  let mySummary = null;
  try {
    mySummary = await api('/leaderboard/me');
  } catch {
    /* rank card degrades gracefully */
  }

  if (!categories.length) {
    page.innerHTML = `<div class="card muted">No topics available yet.</div>`;
    return;
  }

  const state = {
    publishTo: 'everyone', // 'everyone' | 'friend'
    friendId: null,
    friendName: null, // shown on the friend-picker trigger label
    categoryId: categories[0].id,
    categoryName: categories[0].name,
    stakeKobo: 20_000,
    // When true, the stake row's "Other" pill is selected and a
    // numeric input is shown below. `stakeKobo` reflects whatever is
    // in that input (updated live on keystroke without a re-render
    // so focus is preserved).
    stakeIsCustom: false,
    stakeSource: 'bonus', // wire-only hint — the backend chooses the split
    // Per-question answer window in ms. `null` = use the server-side
    // model default (15000). Anti-cheat recommendation for staked
    // duels is 8000–10000; the UI exposes the knob only when
    // stakeKobo > 0 so a free duel doesn't gain a config option it
    // doesn't need. Bounds enforced server-side (5000–30000).
    answerWindowMs: null,
  };

  paint();

  function paint() {
    const stakePills = STAKES.map(
      (s) => `<span class="pill ${!state.stakeIsCustom && state.stakeKobo === s.kobo ? 'on' : ''}"
              data-stake-kobo="${s.kobo}">${escape(s.label)}</span>`,
    ).join('') + `<span class="pill ${state.stakeIsCustom ? 'on' : ''}"
              data-stake-preset="custom">Other</span>`;

    // Custom-amount input renders below the pill row only when the
    // "Other" pill is selected. Keep it a plain type=number so mobile
    // gets the OS numeric keypad. Value shown in ₦; converted to kobo
    // in the input handler.
    const customInputHtml = state.stakeIsCustom ? `
      <div style="margin-top:8px">
        <div class="row" style="gap:6px;align-items:center">
          <span style="font-size:15px;color:#fff;font-weight:600">₦</span>
          <input type="number" inputmode="numeric" id="stake-custom-input"
                 min="${CUSTOM_STAKE_MIN_KOBO / 100}"
                 max="${CUSTOM_STAKE_MAX_KOBO / 100}"
                 step="50"
                 placeholder="Enter amount"
                 value="${state.stakeKobo > 0 ? Math.floor(state.stakeKobo / 100) : ''}"
                 class="field-input"
                 style="flex:1;font-size:14px;padding:9px 12px" />
        </div>
        <div id="stake-custom-hint"
             style="font-size:10.5px;color:var(--mute);font-weight:500;margin-top:4px">
          Between ₦${(CUSTOM_STAKE_MIN_KOBO / 100).toLocaleString()} and
          ₦${(CUSTOM_STAKE_MAX_KOBO / 100).toLocaleString()}.
        </div>
      </div>
    ` : '';

    const bonus = Math.max(0, Number(wallet.bonus_balance_kobo || 0));
    const cash = Math.max(0, Number(wallet.balance_kobo || 0));

    const friendSection = state.publishTo === 'friend'
      ? (friends.length
          ? `<h3 class="sec">Pick a friend</h3>
             ${renderCategoryTrigger('friend-picker', {
               placeholder: 'Choose a friend',
               mark: '👤',
             })}`
          : `<h3 class="sec">Pick a friend</h3>
             <div class="card muted">You have no friends yet.
               <a href="/friends" data-link>Add some</a>.</div>`)
      : '';

    const rankBadge = mySummary && mySummary.rank
      ? `${capitalize(mySummary.tier_key || '')} · Rank #${Number(mySummary.rank).toLocaleString()}`
      : 'Unranked';

    page.innerHTML = `
      <div class="card" style="border-color:#1E6E5C;background:#15323A;padding:12px">
        <div class="row">
          <div class="ic" style="background:#0F352A;color:#5FE0BE">R</div>
          <div class="grow">
            <b style="font-size:13px;color:#fff;font-weight:600">You · ${escape(me.username || 'Player')}</b>
            <div style="font-size:11px;color:#9FE9D2;font-weight:600;margin-top:1px">${escape(rankBadge)}</div>
          </div>
        </div>
      </div>

      <h3 class="sec">Publish to</h3>
      <div class="row" style="gap:9px">
        <div class="opt" data-publish="everyone" style="flex:1;margin:0;justify-content:flex-start;gap:9px;
             ${state.publishTo === 'everyone' ? 'border-color:var(--teal-t);background:#15323A' : ''}">
          <span class="radio ${state.publishTo === 'everyone' ? 'on' : ''}"></span>
          <div><b style="font-size:13px;color:#fff">Everyone</b>
            <div style="font-size:10.5px;color:#7FC3B2;font-weight:600">Public — anyone can join</div></div>
        </div>
        <div class="opt" data-publish="friend" style="flex:1;margin:0;justify-content:flex-start;gap:9px;
             ${state.publishTo === 'friend' ? 'border-color:var(--teal-t);background:#15323A' : ''}">
          <span class="radio ${state.publishTo === 'friend' ? 'on' : ''}"></span>
          <div><b style="font-size:13px;color:#fff">A friend</b>
            <div style="font-size:10.5px;color:var(--mute);font-weight:500">Pick one to challenge</div></div>
        </div>
      </div>

      ${friendSection}

      <h3 class="sec">Topic</h3>
      ${renderCategoryTrigger('topic-picker', {
        placeholder: state.categoryName || 'Choose a topic',
        emphasis: true,
        hint: 'Tap to pick a topic',
      })}

      <h3 class="sec">Stake</h3>
      <div class="row" style="gap:7px;flex-wrap:wrap">${stakePills}</div>
      ${customInputHtml}

      ${state.stakeKobo > 0 || state.stakeIsCustom ? `
        <h3 class="sec">Pay stake with</h3>
        <div class="row" style="gap:9px">
          <div class="opt" data-source="bonus" style="flex:1;margin:0;justify-content:flex-start;gap:9px;
               ${state.stakeSource === 'bonus' ? 'border-color:var(--violet);background:var(--violetSoft)' : ''}">
            <span class="radio ${state.stakeSource === 'bonus' ? 'on' : ''}"></span>
            <div><b style="font-size:13px;color:#fff">Bonus</b>
              <div style="font-size:11px;color:#B7A6FF;font-weight:600">${escape(formatKobo(bonus))}</div></div>
          </div>
          <div class="opt" data-source="main" style="flex:1;margin:0;justify-content:flex-start;gap:9px;
               ${state.stakeSource === 'main' ? 'border-color:var(--teal-t);background:#15323A' : ''}">
            <span class="radio ${state.stakeSource === 'main' ? 'on' : ''}"></span>
            <div><b style="font-size:13px;color:#fff">Main wallet</b>
              <div style="font-size:11px;color:var(--mute);font-weight:500">${escape(formatKobo(cash))}</div></div>
          </div>
        </div>
      ` : ''}

      ${(state.stakeKobo > 0 || state.stakeIsCustom) ? `
        <h3 class="sec">Speed (per question)</h3>
        <div class="row" style="gap:7px;flex-wrap:wrap">
          <span class="pill ${state.answerWindowMs === null ? 'on' : ''}" data-window-ms="">15s (default)</span>
          <span class="pill ${state.answerWindowMs === 10_000 ? 'on' : ''}" data-window-ms="10000">10s</span>
          <span class="pill ${state.answerWindowMs === 8_000 ? 'on' : ''}" data-window-ms="8000">8s (tight)</span>
        </div>
        <div class="muted" style="font-size:11px;margin-top:6px;line-height:1.55">
          Tighter windows raise the cost of scripted play. Both players
          get the same window on this duel.
        </div>
      ` : ''}

      <div class="card" style="border-color:#3A2F5F;background:#1A1530;padding:11px;margin-top:14px">
        <div class="row">
          <div class="ic" style="background:var(--violetSoft);color:#B7A6FF">⏱</div>
          <div class="grow">
            <b style="font-size:12.5px;color:#fff;font-weight:600">How it works</b>
            <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:1px;line-height:1.6">
              You play now · opponent plays within 24h · no-show forfeits to you.
              Unaccepted public duels expire in 24h and refund your stake.
            </div>
          </div>
        </div>
      </div>

      <button class="btn teal" id="publish-btn" style="margin-top:14px">Publish &amp; play my round</button>
    `;

    wire();
  }

  function wire() {
    page.querySelectorAll('[data-publish]').forEach((el) => {
      el.addEventListener('click', () => {
        state.publishTo = el.dataset.publish;
        paint();
      });
    });
    // Topic — searchable bottom-sheet picker over EVERY published
    // category on the platform. Selection updates state without
    // re-painting the whole page: the trigger label auto-updates
    // via the picker's internal `setSelected`.
    attachCategoryPicker('topic-picker', {
      categories,
      selected: { id: state.categoryId, name: state.categoryName },
      title: 'Topic',
      searchPlaceholder: 'Search topics…',
      emptyText: 'No topics match.',
      onChange: (cat) => {
        state.categoryId = cat.id;
        state.categoryName = cat.name;
      },
    });
    page.querySelectorAll('[data-stake-kobo]').forEach((el) => {
      el.addEventListener('click', () => {
        state.stakeKobo = parseInt(el.dataset.stakeKobo, 10) || 0;
        state.stakeIsCustom = false;
        paint();
      });
    });
    const customPill = page.querySelector('[data-stake-preset="custom"]');
    if (customPill) {
      customPill.addEventListener('click', () => {
        state.stakeIsCustom = true;
        // Seed the input with the current preset amount so the user
        // starts from a sensible number rather than a blank field.
        if (state.stakeKobo < CUSTOM_STAKE_MIN_KOBO) {
          state.stakeKobo = CUSTOM_STAKE_MIN_KOBO;
        }
        paint();
        // Focus the input right away — a picker → tap → type flow
        // shouldn't need a second tap.
        const input = document.getElementById('stake-custom-input');
        if (input) input.focus();
      });
    }
    const customInput = document.getElementById('stake-custom-input');
    if (customInput) {
      customInput.addEventListener('input', () => {
        const naira = parseInt(customInput.value || '0', 10);
        state.stakeKobo = Number.isFinite(naira) && naira > 0 ? naira * 100 : 0;
        // Update the pay-source visibility hint inline (no full
        // re-paint so the input keeps focus).
        const hint = document.getElementById('stake-custom-hint');
        if (!hint) return;
        if (state.stakeKobo && state.stakeKobo < CUSTOM_STAKE_MIN_KOBO) {
          hint.textContent = `Minimum stake is ₦${(CUSTOM_STAKE_MIN_KOBO / 100).toLocaleString()}.`;
          hint.style.color = 'var(--pink-t)';
        } else if (state.stakeKobo > CUSTOM_STAKE_MAX_KOBO) {
          hint.textContent = `Maximum stake is ₦${(CUSTOM_STAKE_MAX_KOBO / 100).toLocaleString()}.`;
          hint.style.color = 'var(--pink-t)';
        } else {
          hint.textContent = `Between ₦${(CUSTOM_STAKE_MIN_KOBO / 100).toLocaleString()} and ₦${(CUSTOM_STAKE_MAX_KOBO / 100).toLocaleString()}.`;
          hint.style.color = 'var(--mute)';
        }
      });
    }
    page.querySelectorAll('[data-source]').forEach((el) => {
      el.addEventListener('click', () => {
        state.stakeSource = el.dataset.source;
        paint();
      });
    });
    // Answer-window pill row (only rendered when stake > 0). Empty
    // data-window-ms means "use server default"; other values are
    // sent as-is (server bounds are 5000–30000).
    page.querySelectorAll('[data-window-ms]').forEach((el) => {
      el.addEventListener('click', () => {
        const v = el.dataset.windowMs;
        state.answerWindowMs = v === '' ? null : parseInt(v, 10);
        paint();
      });
    });
    // Friend — same searchable picker, fed with friends shaped as
    // `{id, name}` so it can match on username. `mark: '👤'` on the
    // trigger swaps the topic target emoji for a person glyph.
    // Trigger only rendered when publishTo === 'friend' && friends.length,
    // so a null-safe attach is fine.
    if (state.publishTo === 'friend' && friends.length) {
      const items = friends.map((f) => ({
        id: f.user.id,
        name: f.user.username || 'Player',
      }));
      const currentFriend = state.friendId
        ? items.find((i) => i.id === state.friendId) || null
        : null;
      attachCategoryPicker('friend-picker', {
        categories: items,
        selected: currentFriend,
        title: 'Pick a friend',
        searchPlaceholder: 'Search friends…',
        emptyText: 'No friends match.',
        onChange: (item) => {
          state.friendId = item.id;
          state.friendName = item.name;
        },
      });
    }
    document.getElementById('publish-btn').addEventListener('click', publish);
  }

  async function publish() {
    if (state.publishTo === 'friend' && !state.friendId) {
      toast.error('Pick a friend first.');
      return;
    }
    if (state.stakeIsCustom) {
      if (state.stakeKobo < CUSTOM_STAKE_MIN_KOBO) {
        toast.error(
          `Minimum custom stake is ₦${(CUSTOM_STAKE_MIN_KOBO / 100).toLocaleString()}.`,
        );
        return;
      }
      if (state.stakeKobo > CUSTOM_STAKE_MAX_KOBO) {
        toast.error(
          `Maximum custom stake is ₦${(CUSTOM_STAKE_MAX_KOBO / 100).toLocaleString()}.`,
        );
        return;
      }
    }
    const btn = document.getElementById('publish-btn');
    btn.disabled = true;
    btn.textContent = 'Publishing…';
    try {
      const path = state.publishTo === 'friend' ? '/duels/friend' : '/duels/public';
      const body = {
        category_id: state.categoryId,
        stake_kobo: state.stakeKobo,
        question_count: 10,
      };
      if (state.publishTo === 'friend') body.opponent_id = state.friendId;
      // Only include the answer-window field when the creator picked
      // something other than the default — omit-when-null keeps the
      // request shape identical to the pre-knob build for the majority
      // of duels that stay at 15s.
      if (state.answerWindowMs !== null) body.answer_window_ms = state.answerWindowMs;
      const duel = await api(path, { method: 'POST', body });
      navigate(`/duel/${duel.id}/play`);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Publish & play my round';
      if (err instanceof ApiError && err.code === 'insufficient_balance') {
        toast.error('Not enough balance for that stake.');
      } else if (err instanceof ApiError && err.code === 'feature_disabled') {
        toast.error('Public duels are not available yet.');
      } else {
        toast.error(err.message || 'Could not publish.');
      }
    }
  }
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}
